import {
  BadRequestException,
  ConflictException,
  Injectable,
  NotFoundException,
  Optional,
} from '@nestjs/common';
import {
  CreateProductRequest,
  ProductDetail,
  ProductListQuery,
  ProductListResponse,
  ProductStatsResponse,
  ProductSummary,
  UpdateInventoryRequest,
  UpdateProductRequest,
} from '@cart/shared-types';
import { AuditService } from '../audit/audit.service';
import { StoreContextService } from '../tenancy/store-context.service';
import { ProductStoreScopeService } from './product-store-scope.service';
import { ProductsRepository } from './products.repository';
import { ReadinessService } from '../readiness/readiness.service';

@Injectable()
export class ProductsService {
  constructor(
    private readonly productsRepository: ProductsRepository,
    @Optional()
    private readonly auditService?: AuditService,
    /**
     * Optional so the in-memory specs can build this service bare. Without
     * them a create falls back to the request's own store, which is exactly
     * the single-store behaviour that existed before.
     */
    @Optional()
    private readonly storeContext?: StoreContextService,
    @Optional()
    private readonly scope?: ProductStoreScopeService,
    @Optional()
    private readonly readiness?: ReadinessService
  ) {}

  async list(query: ProductListQuery): Promise<ProductListResponse> {
    this.validateQuery(query);

    const page = Math.max(1, query.page ?? 1);
    const limit = Math.min(100, Math.max(1, query.limit ?? 20));

    const [items, total] = await Promise.all([
      this.productsRepository.list(query),
      this.productsRepository.count(query),
    ]);

    return { items, total, page, limit, totalPages: Math.ceil(total / limit) };
  }

  async adminList(query: ProductListQuery): Promise<ProductListResponse> {
    return this.list({ ...query, includeInactive: true });
  }

  async adminStats(query: ProductListQuery): Promise<ProductStatsResponse> {
    this.validateQuery(query);
    return this.productsRepository.stats({ ...query, includeInactive: true });
  }

  async detail(slug: string): Promise<ProductDetail> {
    if (!slug?.trim()) {
      throw new BadRequestException('slug is required');
    }

    const product = await this.productsRepository.findBySlug(slug.trim());

    if (!product) {
      throw new NotFoundException('Product not found');
    }

    return product;
  }

  async adminDetail(productId: string): Promise<ProductDetail> {
    const product = await this.productsRepository.findByIdFull(productId);

    if (!product) {
      throw new NotFoundException('Product not found');
    }

    return product;
  }

  /**
   * Create the product, in one store or several.
   *
   * `storeIds` is the merchant listing the same thing across their shops. Each
   * one becomes its **own product row**, because `Product.storeId` is singular
   * and everything downstream — stock, variants, price, images — hangs off that
   * row. They are independent from the moment they exist: editing the Bangalore
   * listing does not touch Chennai, which is what a merchant means by "the same
   * product in both shops" far more often than a single shared record would be.
   *
   * Returns the product in the caller's current store when it was one of the
   * targets, so the drawer carries on editing the one the merchant is looking
   * at. Otherwise the first created.
   */
  async create(
    input: CreateProductRequest,
    options: { userId?: string; storeIds?: string[] } = {}
  ): Promise<ProductSummary> {
    this.validateCreateProduct(input);

    const contextStoreId = this.storeContext?.storeId() ?? 'store_demo';

    /*
     * A store must be able to fulfil and invoice an order before it can list
     * something to sell. Enforced here rather than only in the admin UI: a
     * checklist the browser honours is a suggestion, and the request that
     * matters is the one that skips the screen.
     *
     * Optional dependency for the same reason the others here are — the specs
     * construct this service directly, and a missing readiness service must not
     * turn into a silent gate nobody can diagnose.
     */
    if (this.readiness) {
      await this.readiness.assertCanSell(contextStoreId);
    }
    const targets = this.scope && options.userId
      ? await this.scope.resolveTargets(options.userId, options.storeIds, contextStoreId)
      : [contextStoreId];

    const created: ProductSummary[] = [];

    for (const storeId of targets) {
      // Each create runs inside its target store's context, so the repository's
      // own storeId() — and every uniqueness check that depends on it — resolves
      // to that store rather than the one the request arrived on.
      const product = await this.inStore(storeId, async () => {
        if (await this.productsRepository.existsBySlug(input.slug.trim())) {
          // Scoped per store: the same slug in two different shops is a
          // different product, and refusing it would be refusing the whole
          // point of listing across stores.
          throw new ConflictException(
            targets.length > 1
              ? `Product slug already exists in one of the selected stores`
              : 'Product slug already exists'
          );
        }
        return this.productsRepository.create(input);
      });

      created.push(product);

      await this.auditService?.logChange({
        schemaName: 'products',
        tableName: 'products',
        recordId: product.id,
        operation: 'create',
        changedColumns: ['name', 'slug', 'category', 'price', 'stock'],
        newValue: product,
        source: 'ProductsService.create',
      });
    }

    const contextIndex = targets.indexOf(contextStoreId);
    const primary = contextIndex >= 0 ? created[contextIndex] : created[0];
    return created.length > 1 ? { ...primary, createdInStores: created.length } : primary;
  }

  /** Runs `fn` as though the request had arrived scoped to `storeId`. */
  private async inStore<T>(storeId: string, fn: () => Promise<T>): Promise<T> {
    if (!this.storeContext) return fn();
    const current = this.storeContext.current();
    return this.storeContext.run({ ...current, storeId }, fn);
  }

  async update(productId: string, input: UpdateProductRequest): Promise<ProductSummary> {
    this.validateUpdateProduct(input);

    const product = await this.productsRepository.update(productId, input);

    if (!product) {
      throw new NotFoundException('Product not found');
    }

    await this.auditService?.logChange({
      schemaName: 'products',
      tableName: 'products',
      recordId: product.id,
      operation: 'update',
      changedColumns: Object.keys(input),
      newValue: product,
      source: 'ProductsService.update',
    });

    return product;
  }

  async deactivate(productId: string): Promise<ProductSummary> {
    const product = await this.productsRepository.deactivate(productId);

    if (!product) {
      throw new NotFoundException('Product not found');
    }

    await this.auditService?.logChange({
      schemaName: 'products',
      tableName: 'products',
      recordId: product.id,
      operation: 'update',
      changedColumns: ['active'],
      newValue: { productId: product.id, active: false },
      source: 'ProductsService.deactivate',
    });

    return product;
  }

  async updateInventory(productId: string, input: UpdateInventoryRequest): Promise<ProductSummary> {
    if (!Number.isInteger(input.stock) || input.stock < 0) {
      throw new BadRequestException('stock must be zero or greater');
    }

    /*
     * A product with variants has no stock of its own — each variant is a
     * separate line in the inventory ledger and the product total is their sum.
     * Accepting a number here would add a product-level line beside them and
     * count the same goods twice.
     *
     * Rejected rather than redirected to the default variant, for the same
     * reason the price guard rejects: quietly applying a write to a different
     * row than the one addressed makes the API impossible to reason about.
     */
    const variantCount = await this.productsRepository.countActiveVariants?.(productId);
    if (variantCount) {
      throw new BadRequestException(
        'this product is stocked by its variants — set stock on the variant instead'
      );
    }

    const product = await this.productsRepository.updateStock(productId, input.stock);

    if (!product) {
      throw new NotFoundException('Product not found');
    }

    await this.auditService?.logChange({
      schemaName: 'products',
      tableName: 'inventory',
      recordId: product.id,
      operation: 'update',
      changedColumns: ['quantity'],
      newValue: { productId: product.id, stock: product.stock },
      source: 'ProductsService.updateInventory',
    });

    return product;
  }

  private validateQuery(query: ProductListQuery): void {
    if (query.minPrice !== undefined && query.minPrice < 0) {
      throw new BadRequestException('minPrice must be zero or greater');
    }

    if (query.maxPrice !== undefined && query.maxPrice < 0) {
      throw new BadRequestException('maxPrice must be zero or greater');
    }

    if (
      query.minPrice !== undefined &&
      query.maxPrice !== undefined &&
      query.minPrice > query.maxPrice
    ) {
      throw new BadRequestException('minPrice cannot be greater than maxPrice');
    }
  }

  private validateCreateProduct(input: CreateProductRequest): void {
    if (!input.name?.trim()) throw new BadRequestException('name is required');
    if (!input.slug?.trim()) throw new BadRequestException('slug is required');
    if (!input.category?.trim()) throw new BadRequestException('category is required');
    if (!Number.isInteger(input.price) || input.price <= 0) {
      throw new BadRequestException('price must be a positive integer in cents');
    }
    if (!Number.isInteger(input.stock) || input.stock < 0) {
      throw new BadRequestException('stock must be zero or greater');
    }
    this.validateTaxFields(input);
  }

  private validateUpdateProduct(input: UpdateProductRequest): void {
    if (input.name !== undefined && !input.name.trim()) {
      throw new BadRequestException('name cannot be empty');
    }

    if (input.price !== undefined && (!Number.isInteger(input.price) || input.price <= 0)) {
      throw new BadRequestException('price must be a positive integer in cents');
    }

    if (input.categorySlug !== undefined && !input.categorySlug.trim()) {
      throw new BadRequestException('categorySlug cannot be empty');
    }
    this.validateTaxFields(input);
  }

  // Custom-mode products must carry all three rates together (not a
  // partial set), and — same invariant as tax slab profiles
  // (commerce-admin.service.ts's assertGstSplitValid) — CGST + SGST must
  // equal IGST.
  private validateTaxFields(input: {
    taxMode?: string;
    customIgstRateBps?: number;
    customCgstRateBps?: number;
    customSgstRateBps?: number;
  }): void {
    if (input.taxMode !== undefined && input.taxMode !== 'predefined' && input.taxMode !== 'custom') {
      throw new BadRequestException('taxMode must be "predefined" or "custom"');
    }

    const { customIgstRateBps, customCgstRateBps, customSgstRateBps } = input;
    const anyCustomRateProvided =
      customIgstRateBps !== undefined || customCgstRateBps !== undefined || customSgstRateBps !== undefined;

    if (input.taxMode === 'custom' && !anyCustomRateProvided) {
      throw new BadRequestException('custom tax rates are required when taxMode is "custom"');
    }

    if (anyCustomRateProvided) {
      if (customIgstRateBps === undefined || customCgstRateBps === undefined || customSgstRateBps === undefined) {
        throw new BadRequestException(
          'customIgstRateBps, customCgstRateBps, and customSgstRateBps must all be provided together'
        );
      }
      if (customCgstRateBps + customSgstRateBps !== customIgstRateBps) {
        throw new BadRequestException(
          `Custom CGST (${customCgstRateBps}) + SGST (${customSgstRateBps}) must equal IGST (${customIgstRateBps})`
        );
      }
    }
  }
}
